import React, { useEffect, useState } from "react";
import { BusinessCreditReportStatePII } from "../../../store/businessCreditReportService/types";
import { getFormattedDate } from "../../../util/dateFormatting";
import i18nService from "../../../util/Internationalization/i18n/i18n";

interface IProps {
    reportObject: BusinessCreditReportStatePII;
    view: string;
    isBankrupt: boolean;
}

const BCreditRiskIndicator: React.FunctionComponent<IProps> = (props) => {
    const [color, setColor] = useState("");
    const [ratingBadge, setRatingBadge] = useState("");
    const [ratingText, setRatingText] = useState(
        "Rating Last Changed: " +
            getFormattedDate(
                i18nService.get("Country"),
                props.reportObject?.businessCompanyReport?.credit_score_and_limit?.date_of_latest_rating_Change
                    ? props.reportObject?.businessCompanyReport?.credit_score_and_limit?.date_of_latest_rating_Change
                    : ""
            )
    );

    let score = props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_score
        ? props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_score
        : "NR";
    let scoreToScale: any = ""; // D&B uses 0-5 rating, 0 = bankrupt, 1 = best, 5 = worst
    let previousOffset = 0;
    let circleVal = 0.73; // % of circle that is visible (1 would be a full circle)

    // update score rating to 0-100 to follow same scale as svg / select color per rating
    const handleRating = () => {
        if (props.isBankrupt) {
            score = "0";
        }

        switch (score) {
            case "0":
                scoreToScale = "0";
                setColor("lvl-very-high-risk");
                setRatingBadge("Bankrupt");
                setRatingText("“X” denotes open bankruptcy, out of business at the location or higher risk situations");
                break;
            case "1":
                scoreToScale = "90";
                setColor("lvl-very-low-risk");
                setRatingBadge("Very Low Risk");
                break;
            case "2":
                scoreToScale = "70";
                setColor("lvl-low-risk");
                setRatingBadge("Low Risk");
                break;
            case "3":
                scoreToScale = "50";
                setColor("lvl-moderate-risk");
                setRatingBadge("Moderate Risk");
                break;
            case "4":
                scoreToScale = "30";
                setColor("lvl-high-risk");
                setRatingBadge("High Risk");
                break;
            case "5":
                scoreToScale = "10";
                setColor("lvl-very-high-risk");
                setRatingBadge("Very High Risk");
                break;
            default:
                scoreToScale = "0";
                setColor("lvl-not-rated-risk");
                setRatingBadge("Not Rated");
                setRatingText("Your profile doesn't include any past Credit Risk Ratings at this time");
                break;
        }
    };

    // Builds SVG ARC attributes
    const loadScoreGraphic = () => {
        let circles = document.getElementsByClassName("circle");
        [...circles].forEach((el) => {
            let segVal = el.getAttribute("data-array");
            let segReduce = el.getAttribute("data-seg-reduce");
            let data = segmentVal(segVal, segReduce);
            el.setAttribute("stroke-dasharray", data.array + ", 100");
            el.setAttribute("stroke-dashoffset", data.offset);
        });
    };

    const segmentVal = (segVal: any, segReduce: any) => {
        // Size of gap between segments. Gap size is 2.3
        let halfGapVal = 1.15;
        // Calculate dash size
        let dashArray = circleVal * segVal - halfGapVal * segReduce;
        // Calculate offset value
        let dashOffset: any = previousOffset;
        // Update previous offset value
        previousOffset = dashArray + halfGapVal * 2;
        // Reduce segment by offset value
        dashArray = dashArray - dashOffset;
        // If this is first segment, offset = 0
        dashOffset = segReduce === 1 ? 0 : (dashOffset * -1).toFixed(2);

        return { array: dashArray.toFixed(2), offset: dashOffset };
    };

    // Positions score dot on semi-circle
    const positionScoreDot = () => {
        let dots = document.getElementsByClassName("dot");
        let offset = scoreToScale * circleVal * -1;
        [...dots].forEach((el) => {
            el.setAttribute("stroke-dashoffset", offset.toString());
        });
    };

    const ensureDataFetched = () => {
        handleRating();
        loadScoreGraphic();
        positionScoreDot();
    };

    useEffect(ensureDataFetched);

    return (
        <div className="graph-img">
            <svg viewBox="0 4 37 37" className="circular-chart">
                <path
                    className={props.view !== "NR" ? "circle lvl-very-high-risk" : "circle lvl-not-rated-risk"}
                    data-array="19"
                    data-seg-reduce="0"
                    d="M4,33 A17,17 0 1,1 32,33"
                />
                <path
                    className={props.view !== "NR" ? "circle lvl-high-risk" : "circle lvl-not-rated-risk"}
                    data-array="40"
                    data-seg-reduce="1"
                    d="M4,33 A17,17 0 1,1 32,33"
                />
                <path
                    className={props.view !== "NR" ? "circle lvl-moderate-risk" : "circle lvl-not-rated-risk"}
                    data-array="60"
                    data-seg-reduce="0"
                    d="M4,33 A17,17 0 1,1 32,33"
                />
                <path
                    className={props.view !== "NR" ? "circle lvl-low-risk" : "circle lvl-not-rated-risk"}
                    data-array="80"
                    data-seg-reduce="1"
                    d="M4,33 A17,17 0 1,1 32,33"
                />
                <path
                    className={props.view !== "NR" ? "circle lvl-very-low-risk" : "circle lvl-not-rated-risk"}
                    data-array="100"
                    data-seg-reduce="0"
                    d="M4,33 A17,17 0 1,1 32,33"
                />
                <path className={"dot dot-border " + color} stroke-dasharray="0, 100" d="M4,33 A17,17 0 1,1 32,33" />
                <path className="dot" stroke-dasharray="0, 100" d="M4,33 A17,17 0 1,1 32,33" />
            </svg>
            <div className={score !== "0" && score !== "NR" ? "graph-text" : "graph-text small"}>
                <div className="graph-value">
                    <div className={"score-rating " + color + "-color"}>{score === "0" || props.isBankrupt ? "X" : score}</div>
                    <div className="score-description">{score !== "0" && score !== "NR" ? "OUT OF 5" : ""}</div>
                </div>
                <div className="graph-details">
                    <div className={"risk-badge " + color + "-color " + color + "-lightbg"}>{ratingBadge}</div>
                    {props.view === "report" && <div>{ratingText}</div>}
                </div>
            </div>
        </div>
    );
};

export default BCreditRiskIndicator;
